/**
 * Attendance + project labour helpers — web SDK port of
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/attendance/attendance.ts
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/attendance/types.ts
 *
 * Schema parity is mandatory. Mobile + web read AND write to the
 * same two collections:
 *   - `attendance/{projectId_labourId_date}`     — one doc per
 *                                                  labourer per day
 *   - `projectLabour/{projectId_labourId}`        — roster doc
 *
 * Mobile uses these doc-id conventions so `markAttendance` is
 * idempotent (marking twice on the same day patches the same doc).
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';

import { db } from '@/lib/firebase';

// ───── Types (verbatim from mobile) ─────

export type AttendanceStatus =
  | 'present'
  | 'absent'
  | 'half_day'
  | 'paid_leave'
  | 'week_off';

export type AttendanceUiStatus = AttendanceStatus | 'unmarked' | 'loading';

export type ProjectLabour = {
  id: string;
  orgId: string;
  projectId: string;
  labourId: string;
  labourName: string;
  labourRole: string;
  description?: string;
  payRate?: number;
  payUnit?: 'day' | 'hour';
  disabled?: boolean;
  createdBy: string;
  createdAt: { toMillis(): number } | null;
  updatedAt?: { toMillis(): number } | null;
};

export type AttendanceRecord = {
  id: string;
  orgId: string;
  projectId: string;
  labourId: string;
  labourName: string;
  labourRole: string;
  description?: string;
  payRate?: number;
  payUnit?: 'day' | 'hour';
  disabled?: boolean;
  date: string; // YYYY-MM-DD
  status: AttendanceStatus;
  createdBy: string;
  createdAt: { toMillis(): number } | null;
};

// ───── Doc id helpers ─────

export function getProjectLabourDocId(
  projectId: string,
  labourId: string,
): string {
  return `${projectId}_${labourId}`;
}

export function getAttendanceDocId(
  projectId: string,
  labourId: string,
  dateString: string,
): string {
  return `${projectId}_${labourId}_${dateString}`;
}

// ───── Write helpers ─────

export type UpsertProjectLabourInput = {
  orgId: string;
  projectId: string;
  labourId: string;
  labourName: string;
  labourRole: string;
  description?: string;
  payRate?: number;
  payUnit?: 'day' | 'hour';
  createdBy: string;
  disabled?: boolean;
};

export async function upsertProjectLabour(
  input: UpsertProjectLabourInput,
): Promise<string> {
  const docId = getProjectLabourDocId(input.projectId, input.labourId);
  const ref = doc(db, 'projectLabour', docId);
  const payload: Record<string, unknown> = {
    orgId: input.orgId,
    projectId: input.projectId,
    labourId: input.labourId,
    labourName: input.labourName,
    labourRole: input.labourRole,
    disabled: !!input.disabled,
    createdBy: input.createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  if (input.description) payload.description = input.description;
  if (input.payRate !== undefined) payload.payRate = input.payRate;
  if (input.payUnit) payload.payUnit = input.payUnit;
  await setDoc(ref, payload, { merge: true });
  return docId;
}

export type MarkAttendanceInput = {
  orgId: string;
  projectId: string;
  labourId: string;
  labourName: string;
  labourRole: string;
  description?: string;
  payRate?: number;
  payUnit?: 'day' | 'hour';
  date: string; // YYYY-MM-DD
  status: AttendanceStatus;
  createdBy: string;
};

/**
 * Upsert attendance for one labourer on one date. Auto-upserts the
 * `projectLabour` roster doc too so the roster stays in sync.
 *
 * Same doc-id convention as mobile so marking twice on the same day
 * patches the same doc (no duplicates).
 */
export async function markAttendance(
  input: MarkAttendanceInput,
): Promise<string> {
  // Keep the roster doc fresh (idempotent).
  await upsertProjectLabour({
    orgId: input.orgId,
    projectId: input.projectId,
    labourId: input.labourId,
    labourName: input.labourName,
    labourRole: input.labourRole,
    description: input.description,
    payRate: input.payRate,
    payUnit: input.payUnit,
    createdBy: input.createdBy,
    disabled: false,
  });

  const docId = getAttendanceDocId(
    input.projectId,
    input.labourId,
    input.date,
  );
  const ref = doc(db, 'attendance', docId);
  const payload: Record<string, unknown> = {
    orgId: input.orgId,
    projectId: input.projectId,
    labourId: input.labourId,
    labourName: input.labourName,
    labourRole: input.labourRole,
    disabled: false,
    date: input.date,
    status: input.status,
    createdBy: input.createdBy,
    createdAt: serverTimestamp(),
  };
  if (input.description) payload.description = input.description;
  if (input.payRate !== undefined) payload.payRate = input.payRate;
  if (input.payUnit) payload.payUnit = input.payUnit;
  await setDoc(ref, payload);
  return docId;
}

/** Update just the status field of an existing record. */
export async function updateAttendanceStatus(
  recordId: string,
  status: AttendanceStatus,
): Promise<void> {
  await updateDoc(doc(db, 'attendance', recordId), { status });
}

export type UpdateAttendanceRecordInput = Partial<{
  labourName: string;
  labourRole: string;
  description: string;
  payRate: number;
  payUnit: 'day' | 'hour';
  status: AttendanceStatus;
  disabled: boolean;
}>;

export async function updateAttendanceRecord(
  recordId: string,
  input: UpdateAttendanceRecordInput,
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (input.labourName !== undefined) payload.labourName = input.labourName;
  if (input.labourRole !== undefined) payload.labourRole = input.labourRole;
  if (input.description !== undefined) payload.description = input.description;
  if (input.payRate !== undefined) payload.payRate = input.payRate;
  if (input.payUnit !== undefined) payload.payUnit = input.payUnit;
  if (input.status !== undefined) payload.status = input.status;
  if (input.disabled !== undefined) payload.disabled = input.disabled;
  if (Object.keys(payload).length === 0) return;
  await updateDoc(doc(db, 'attendance', recordId), payload);
}

export async function updateProjectLabour(
  projectId: string,
  labourId: string,
  input: Partial<
    Pick<
      ProjectLabour,
      'labourName' | 'labourRole' | 'description' | 'payRate' | 'payUnit' | 'disabled'
    >
  >,
): Promise<void> {
  const payload: Record<string, unknown> = { updatedAt: serverTimestamp() };
  if (input.labourName !== undefined) payload.labourName = input.labourName;
  if (input.labourRole !== undefined) payload.labourRole = input.labourRole;
  if (input.description !== undefined) payload.description = input.description;
  if (input.payRate !== undefined) payload.payRate = input.payRate;
  if (input.payUnit !== undefined) payload.payUnit = input.payUnit;
  if (input.disabled !== undefined) payload.disabled = input.disabled;
  await setDoc(
    doc(db, 'projectLabour', getProjectLabourDocId(projectId, labourId)),
    payload,
    { merge: true },
  );
}

/**
 * Soft-disable a labourer across every attendance record on the
 * project. Used by the labour edit form's "Remove from roster"
 * button (mirrors mobile's `setLabourDisabledAcrossProject`).
 */
export async function setLabourDisabledAcrossProject(
  projectId: string,
  labourId: string,
  disabled: boolean,
): Promise<number> {
  await updateProjectLabour(projectId, labourId, { disabled });
  const snap = await getDocs(
    query(collection(db, 'attendance'), where('projectId', '==', projectId)),
  );
  const docs = snap.docs.filter(
    (d) => (d.data() as { labourId?: string }).labourId === labourId,
  );
  if (docs.length === 0) return 0;
  for (let i = 0; i < docs.length; i += 400) {
    const chunk = docs.slice(i, i + 400);
    const batch = writeBatch(db);
    for (const d of chunk) batch.update(d.ref, { disabled });
    await batch.commit();
  }
  return docs.length;
}

/**
 * Hard delete: removes the roster doc and every attendance record
 * for this labourer on the project. Use sparingly — soft-disable is
 * usually safer.
 */
export async function deleteLabourAcrossProject(
  projectId: string,
  labourId: string,
): Promise<number> {
  const rosterRef = doc(
    db,
    'projectLabour',
    getProjectLabourDocId(projectId, labourId),
  );
  const rosterSnap = await getDoc(rosterRef);
  if (rosterSnap.exists()) {
    const batch = writeBatch(db);
    batch.delete(rosterRef);
    await batch.commit();
  }
  const snap = await getDocs(
    query(collection(db, 'attendance'), where('projectId', '==', projectId)),
  );
  const docs = snap.docs.filter(
    (d) => (d.data() as { labourId?: string }).labourId === labourId,
  );
  if (docs.length === 0) return 0;
  for (let i = 0; i < docs.length; i += 400) {
    const chunk = docs.slice(i, i + 400);
    const batch = writeBatch(db);
    for (const d of chunk) batch.delete(d.ref);
    await batch.commit();
  }
  return docs.length;
}

// ───── Display helpers ─────

export const STATUS_LABEL: Record<AttendanceUiStatus, string> = {
  present: 'Present',
  half_day: 'Half',
  absent: 'Absent',
  paid_leave: 'Paid leave',
  week_off: 'Week off',
  unmarked: 'Unmarked',
  loading: '…',
};

/** Payroll-unit weight per status. Mirrors mobile's calc:
 *  Present = 1 unit, Half = 0.5, others = 0. */
export function payableUnitFor(status: AttendanceUiStatus): number {
  if (status === 'present') return 1;
  if (status === 'half_day') return 0.5;
  return 0;
}

/** Hours per status (8 / 4 / 0). Used for the day-summary headline. */
export function hoursFor(status: AttendanceUiStatus): number {
  if (status === 'present') return 8;
  if (status === 'half_day') return 4;
  return 0;
}

/** YYYY-MM-DD in local timezone. */
export function toLocalDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function todayLocalDateKey(): string {
  return toLocalDateKey(new Date());
}

export function shiftDateKey(key: string, deltaDays: number): string {
  const [y, m, d] = key.split('-').map((s) => parseInt(s, 10));
  const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
  dt.setDate(dt.getDate() + deltaDays);
  return toLocalDateKey(dt);
}

export function formatLongDate(key: string): string {
  const [y, m, d] = key.split('-').map((s) => parseInt(s, 10));
  const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
  return dt.toLocaleDateString('en-IN', {
    weekday: 'long',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** Stable tint per labour name — keeps avatar color consistent
 *  across re-renders + matches the mobile palette. */
const AVATAR_TINTS: { bg: string; fg: string }[] = [
  { bg: '#FCE7F3', fg: '#BE185D' },
  { bg: '#DBEAFE', fg: '#1D4ED8' },
  { bg: '#D1FAE5', fg: '#047857' },
  { bg: '#FEF3C7', fg: '#92400E' },
  { bg: '#FFEDD5', fg: '#9A3412' },
  { bg: '#E0E7FF', fg: '#3730A3' },
  { bg: '#F3E8FF', fg: '#6D28D9' },
  { bg: '#CFFAFE', fg: '#0E7490' },
];

export function avatarTintFor(name: string): { bg: string; fg: string } {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return AVATAR_TINTS[Math.abs(h) % AVATAR_TINTS.length];
}

export function initialOf(name: string | undefined): string {
  const c = (name ?? '').trim().charAt(0);
  return c ? c.toUpperCase() : '?';
}
